import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CdkTableModule } from '@angular/cdk/table';
import { HeaderComponent } from '../shared/header.component';
import { Observable, Subject, catchError, map, of, switchMap, tap, throwError } from 'rxjs';
import { SessionDto, UpdatePostDto } from '@oracychampions/api-interfaces';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions, FormlyModule } from '@ngx-formly/core';
import { FormlyBootstrapModule } from '@ngx-formly/bootstrap';
import { ToastrService } from 'ngx-toastr';
import { v4 as uuid } from 'uuid';
import { GroupsService } from '../group/groups.service';
import { Game } from '../core/game';
import { CountdownComponent } from './countdown.component';

@Component({
  selector: 'oc-session-participate',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    CdkTableModule,
    HeaderComponent,
    ReactiveFormsModule,
    FormlyBootstrapModule,
    FormlyModule,
    CountdownComponent,
  ],
  template: `
    <div class="oc-session-participate">
      <div
        class="oc-session-participate-instructions"
        [class.game-none]="gameInfo.game === Game.None"
        [class.game-yesno]="gameInfo.game === Game.YesNo"
        [class.game-debate]="gameInfo.game === Game.Debate"
        [class.picture]="gameInfo.game === Game.Picture"
      >
        <ng-container *ngIf="gameInfo.game === Game.None">
          <h1>Voice note</h1>
          <p>Record anything you want!</p>
        </ng-container>
        <ng-container *ngIf="gameInfo.game === Game.YesNo">
          <!-- <h1>Yes/No Game</h1>
          <p>
            A student takes the hot seat and is asked questions that they are not allowed to say Yes or No to. When they
            make a mistake another student takes the hot seat.
          </p> -->
        </ng-container>
        <ng-container *ngIf="gameInfo.game === Game.Debate">
          <h1>Debate</h1>
          <p>Choose a debating topic, split into two teams and argue for or against the point.</p>
        </ng-container>
        <ng-container *ngIf="gameInfo.game === Game.Picture">
          <div class="picture-game">
            <div class="d-flex align-items-center justify-content-between mb-3">
              <h1 class="m-0">Say It, See It</h1>
              <a *ngIf="!recordingPanel" (click)="cancel()" class="btn btn-outline-dark">Close</a>
            </div>
            <div class="photo">
              <div class="photo-frame">
                <img *ngIf="imageUrl" [src]="imageUrl" style="width: 100%" />
                <div *ngIf="saving && !imageUrl" class="photo-frame-loading"></div>
              </div>
            </div>
          </div>
        </ng-container>
      </div>
      <div *ngIf="recordingPanel" class="oc-session-participate-form">
        <div *ngIf="!started && !countingDown">
          <div class="btn btn-primary btn-lg" (click)="countdown()">Start</div>
        </div>
        <div *ngIf="countingDown">
          <oc-countdown (completed)="countdownComplete()"></oc-countdown>
        </div>

        <form *ngIf="started" [formGroup]="form" (ngSubmit)="onSubmit(model)">
          <formly-form [form]="form" [fields]="fields" [model]="model" [options]="options"></formly-form>
          <div class="d-flex gap-2">
            <button type="submit" class="btn btn-primary mt-3 mb-4">
              <span *ngIf="saving" class="spinner-border spinner-border-sm"></span>
              <span *ngIf="!saving">Finish</span>
            </button>
            <button type="button" class="btn btn-secondary mt-3 mb-4" (click)="cancel()">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  `,
  styles: `
    .oc-session-participate {
      width: 100%;
      height: 100vh;
      display: flex;
      background: white;
    }

    .oc-session-participate-instructions {
      flex: 1;
      background-color: #ccc;
      padding: 4rem;
      color: rgba(0,0,0,0.6);

      h1 {
        margin-bottom: 2.5rem;
        font-size: 2.5rem;
      }

      p {
        font-size: 1.7rem;
        line-height: 1.4;
      }
    }

    .oc-session-participate-form {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }

    .game-none {
      background-color: #eee;
    }

    .game-yesno {
      background-image: url('/background-2.jpg');
      background-size: cover;
      background-position: center;
    }

    .game-debate {
      background-color: #f66c6c;
    }

    .oc-session-participate-form {
      min-width: 440px;
      height: 100%;
      padding: 2rem;
    }

    .picture-game {
      display: flex;
      flex-direction: column;
      height: calc(100vh - 8rem);
    }

    .photo {
      background: white;
      padding: 1rem;
      padding-bottom: 3rem;
      flex: 1;
      aspect-ratio: 1 / 1;
      height: calc(100vh - 8rem - 70px);
      width: auto;
      margin: auto;
    }

    .photo-frame {
      background: #222;
      height: 100%;
      position: relative;

      .photo-frame-loading {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: url('/build.gif') no-repeat center;
      }
    }

    
  `,
})
export class SessionParticipateComponent {
  private route = inject(ActivatedRoute);
  private toastr = inject(ToastrService);
  private groupsService = inject(GroupsService);
  private router = inject(Router);

  private stop$ = new Subject<boolean>();

  recordingPanel = true;

  session$: Observable<SessionDto>;
  gameInfo$: Observable<any>;

  imageUrl = '';

  form = new FormGroup({});
  model = { audio: null };
  fields: FormlyFieldConfig[];

  Game = Game;

  saving = false;
  options: FormlyFormOptions = {
    formState: {
      submitted: false,
    },
  };

  session: any;
  gameInfo: any;
  started = false;
  countingDown = false;

  constructor() {
    this.session$ = this.route.parent!.data.pipe(
      map((data) => data['session']),
      tap((session) => (this.session = session)),
    );

    this.gameInfo$ = this.route.params.pipe(
      map((params) => params['info']),
      map((info) => atob(info)),
      map((info) => JSON.parse(info)),
      tap((info) => (this.gameInfo = info)),
    );

    this.session$.subscribe();
    this.gameInfo$.subscribe();
  }

  ngOnInit() {
    this.fields = [
      {
        key: 'audio',
        type: 'record-audio',
        props: {
          label: '',
          hideRequiredMarker: true,
          required: true,
          autoStart: true,
          stop: this.stop$,
          visualise: true,
        },
      },
    ];
  }

  countdown() {
    this.countingDown = true;
  }

  countdownComplete() {
    this.countingDown = false;
    this.started = true;
  }

  onSubmit(model: any) {
    this.options.formState.submitted = true;
    if (this.saving) {
      return;
    }

    this.stop$.next(true);

    setTimeout(() => {
      if (!this.form.valid) {
        this.toastr.error('Please review and try again');
        return;
      }

      this.saving = true;

      const audioId = uuid();

      const post: UpdatePostDto = {
        title: this.session.name,
        game: this.gameInfo.game,
        audioId,
        levels: JSON.stringify(model.audio.levels),
        groupId: this.session.groupId,
        students: Object.keys(this.gameInfo.students).map((id) => ({ id })),
        sessionId: this.session.id,
      };

      this.groupsService
        .uploadAudio(audioId, model.audio.blob)
        .pipe(
          switchMap(() => {
            if (this.gameInfo.game !== Game.Picture) {
              return of(null);
            }
            return this.groupsService.transcribe(audioId, model.audio.blob).pipe(
              switchMap(({ text }) => {
                return this.groupsService.generateImage(text);
              }),
              catchError((error) => {
                return throwError(() => new Error(error.error.error.message));
              }),
              tap((response: any) => {
                console.log(response);
                this.imageUrl = response.data[0].url;
              }),
              switchMap((response: any) => {
                return this.groupsService.uploadImageUrl(response.data[0].url);
              }),
            );
          }),
          switchMap((response: any) => {
            console.log(response);
            return this.groupsService.createPost({ ...post, imageId: response?.imageId });
          }),
        )
        .subscribe({
          next: (response) => {
            this.toastr.success('Post created');
            this.recordingPanel = false;
            if (this.gameInfo.game !== Game.Picture) {
              this.router.navigate(['session', this.session.id, 'dashboard']);
            }
          },
          error: (error) => {
            this.toastr.error(error.message);
            this.form = new FormGroup({});
            this.model = { audio: null };
            this.saving = false;
          },
        });
    }, 50);
  }

  cancel() {
    this.router.navigate(['session', this.session.id, 'dashboard']);
  }
}
